import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Eye, EyeOff } from 'lucide-react'
import mixpanel from 'mixpanel-browser'

const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden>
    <path fill="#000" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#000" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#000" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#000" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
)

type InviteLoginDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  inviteId: string
  orgName: string | null
  onSuccess: () => void
}

export function InviteLoginDialog({
  open,
  onOpenChange,
  inviteId,
  orgName,
  onSuccess,
}: InviteLoginDialogProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const { toast } = useToast()
  const navigate = useNavigate()

  const returnPath = `/accept-invite?org_invite_id=${inviteId}`

  const handleGoogleLogin = async () => {
    mixpanel.track('invite_login_google_clicked', { invite_id: inviteId })
    setGoogleLoading(true)
    try {
      const nextParam = encodeURIComponent(returnPath)
      const callbackUrl = `${window.location.origin}/auth/callback?next=${nextParam}`
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: callbackUrl,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      })
      if (error) {
        toast({ title: 'Erro no login com Google', description: error.message, variant: 'destructive' })
        setGoogleLoading(false)
      }
    } catch {
      toast({ title: 'Erro inesperado', description: 'Não foi possível continuar com Google.', variant: 'destructive' })
      setGoogleLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })

      if (error) {
        const msg = error.message.toLowerCase()
        if (
          msg.includes('email not confirmed') ||
          msg.includes('email_not_confirmed') ||
          msg.includes('email not verified') ||
          msg.includes('email_not_verified')
        ) {
          toast({
            title: 'E-mail não confirmado',
            description: 'Confirme seu e-mail antes de entrar. Verifique sua caixa de entrada.',
            variant: 'destructive',
          })
          onOpenChange(false)
          navigate('/verify-email', { state: { email } })
          return
        }
        if (msg.includes('invalid login credentials') || msg.includes('invalid credentials')) {
          toast({ title: 'Credenciais inválidas', description: 'E-mail ou senha incorretos.', variant: 'destructive' })
          return
        }
        toast({ title: 'Erro no login', description: error.message, variant: 'destructive' })
        return
      }

      if (!data?.user?.email_confirmed_at) {
        await supabase.auth.signOut()
        toast({
          title: 'E-mail não confirmado',
          description: 'Confirme seu e-mail antes de entrar.',
          variant: 'destructive',
        })
        onOpenChange(false)
        navigate('/verify-email', { state: { email } })
        return
      }

      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser()
      if (userError || !currentUser?.email_confirmed_at) {
        await supabase.auth.signOut()
        toast({ title: 'Não foi possível verificar a conta', description: 'Tente novamente.', variant: 'destructive' })
        return
      }

      const { data: accepted, error: acceptError } = await supabase.rpc('accept_organization_invite', {
        invite_id: inviteId,
      })

      if (acceptError) {
        toast({
          title: 'Não foi possível aceitar o convite',
          description: acceptError.message || 'Tente novamente em instantes.',
          variant: 'destructive',
        })
        return
      }

      if (!accepted) {
        await supabase.auth.signOut()
        toast({
          title: 'Convite não pôde ser aceito',
          description: 'Use o mesmo e-mail para o qual o convite foi enviado.',
          variant: 'destructive',
        })
        return
      }

      mixpanel.track('invite_login_success', { invite_id: inviteId, method: 'email' })
      mixpanel.identify(currentUser.id)
      mixpanel.people.set({
        $email: currentUser.email,
        $name: currentUser.user_metadata?.full_name || currentUser.email,
      })

      toast({
        title: 'Convite aceito!',
        description: 'Entrando na aplicação...',
      })
      setEmail('')
      setPassword('')
      onOpenChange(false)
      onSuccess()
    } catch {
      await supabase.auth.signOut()
      toast({ title: 'Erro inesperado', description: 'Tente novamente.', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[400px] gap-0 border-[#e2e8f0] p-0 overflow-hidden">
        <DialogHeader className="space-y-1 border-b border-[#f1f5f9] px-6 py-5 text-left">
          <DialogTitle className="text-xl font-semibold text-[#0f172a]">Entrar na sua conta</DialogTitle>
          <DialogDescription className="text-sm text-[#64748b]">
            {orgName
              ? `Use sua conta Fluity para aceitar o convite de ${orgName}.`
              : 'Use sua conta Fluity para aceitar o convite da equipe.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5 px-6 py-6">
          <div className="flex flex-col gap-2">
            <Label htmlFor="invite-login-email" className="text-[#0f172a]">
              E-mail
            </Label>
            <Input
              id="invite-login-email"
              type="email"
              autoComplete="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-10 rounded-md border-[#e2e8f0]"
              required
              disabled={loading || googleLoading}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="invite-login-password" className="text-[#0f172a]">
              Senha
            </Label>
            <div className="relative">
              <Input
                id="invite-login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10 rounded-md border-[#e2e8f0] pr-10"
                required
                disabled={loading || googleLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading || googleLoading}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading || googleLoading}
            className="h-11 w-full rounded-xl bg-[#2563EB] text-base font-medium hover:bg-[#1d4ed8]"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Entrando...
              </span>
            ) : (
              'Entrar'
            )}
          </Button>

          <div className="relative flex items-center py-1">
            <span className="flex-1 border-t border-[#e2e8f0]" />
            <span className="mx-3 text-[11px] uppercase tracking-wide text-[#94a3b8]">ou</span>
            <span className="flex-1 border-t border-[#e2e8f0]" />
          </div>

          <Button
            type="button"
            variant="outline"
            disabled={loading || googleLoading}
            onClick={handleGoogleLogin}
            className="h-11 w-full rounded-full border-[#e2e8f0] bg-[#f8fafc] text-[#0f172a] hover:bg-[#f1f5f9]"
          >
            {googleLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                Conectando...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <GoogleIcon />
                Continuar com Google
              </span>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
